import useRegisteredModuleFilters from '../../moduleFilters';
import React, { useState } from 'react';
import { useSession } from '@/app/providers/session-provider';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { ConfirmActionDialog, PageDivider, PageFrame } from '../../../shared/components/patterns';
import { toast } from '../../../shared/hooks/use-toast';
import { useLinkedEntityCards } from '../../../shared/components/linked-entities/LinkedEntityCardHost';
import {
  CompletedHeader,
  CompletedStats,
  CompletedBoard,
  CompletedDetailDialog,
} from '@/modules/deals/components/completed';
import DealsFiltersBar from '@/modules/deals/components/shared/DealsFiltersBar';
import { useCompletedDealsData } from '@/modules/deals/hooks';
import '../styles/deals.css';

const CompletedDealsPage = () => {
  const { capabilities } = useSession();
  const { openEntityCard } = useLinkedEntityCards();
  const {
    visibleStages,
    dealsWithoutSettlement,
    dealsToSettle,
    settledDeals,
    stats,
    completedDealsModuleDisabled,
    completedDealsError,
    completedDealsTenantDatabaseBlocked,
    settlementBatchState,
    selectedDeal,
    isDetailDialogOpen,
    staff,
    staffUnavailable,
    staffCatalogStatus,
    clients,
    activeClientFilterLabel,
    isClientFiltered,
    filters,
    activeOverviewFilter,
    assigneeOptions,
    hasActiveFilters,
    setSearchTerm,
    setStatusFilter,
    setAssigneeFilter,
    setStageFilter,
    setClientFilter,
    clearFilters,
    setOverviewFilter,
    openDealDetails,
    closeDealDetails,
    openCompletedDealCreateInvoice,
    openCompletedDealAccounting,
    onDragEnd,
    handleDeleteCompletedDeal,
    handleUpdateCompletedDealStaff,
  } = useCompletedDealsData();
  const moduleFilters = useRegisteredModuleFilters('completedDeals', [...(dealsWithoutSettlement || []), ...(dealsToSettle || []), ...(settledDeals || [])]);
  const [deleteRequest, setDeleteRequest] = useState(null);

  function openClientDetail(clientId) {
    openEntityCard({ entityType: 'client', entityId: clientId });
  }

  function openResponsibleStaff(staffId, staffMember) {
    const resolvedId = staffId || staffMember?.id;
    if (resolvedId) openEntityCard({ entityType: 'staff', entityId: resolvedId });
  }

  return (
    <PageFrame filterModel={moduleFilters} className="deals-page">
      {!completedDealsModuleDisabled ? <CompletedHeader /> : null}

      {completedDealsModuleDisabled ? (
        <Alert className="deals-alert">
          <AlertDescription>
            {completedDealsError || 'Modulo affari non abilitato per questo workspace.'}
          </AlertDescription>
        </Alert>
      ) : null}

      {!completedDealsModuleDisabled && completedDealsTenantDatabaseBlocked ? (
        <Alert className="deals-alert">
          <AlertDescription>
            {completedDealsError || 'Database tenant non disponibile per il modulo affari.'}
          </AlertDescription>
        </Alert>
      ) : null}

      {!completedDealsModuleDisabled && !completedDealsTenantDatabaseBlocked && isClientFiltered ? (
        <Alert variant="info" className="deals-alert deals-alert--client">
          <AlertDescription>
            Vista filtrata per cliente: {activeClientFilterLabel}.
          </AlertDescription>
        </Alert>
      ) : null}

      {!completedDealsModuleDisabled && !completedDealsTenantDatabaseBlocked && staffUnavailable ? (
        <Alert className="deals-alert">
          <AlertDescription>
            Catalogo staff non disponibile dal backend. Le azioni staff-correlate nel flow completed sono momentaneamente degradate.
          </AlertDescription>
        </Alert>
      ) : null}

      {!completedDealsModuleDisabled && !completedDealsTenantDatabaseBlocked ? (
        <CompletedStats
          stats={stats}
          activeMetric={activeOverviewFilter}
          onMetricClick={setOverviewFilter}
        />
      ) : null}

      {!completedDealsModuleDisabled && !completedDealsTenantDatabaseBlocked ? (
        <>
        <PageDivider />
        <DealsFiltersBar
          searchValue={filters.searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Cerca per nome, cliente, responsabile..."
          hasActiveFilters={hasActiveFilters}
          onReset={clearFilters}
          filters={[
            {
              id: 'status',
              value: filters.statusFilter,
              onChange: setStatusFilter,
              placeholder: 'Stato',
              options: [
                { value: 'all', label: 'Tutti gli stati' },
                { value: 'unavailable', label: 'Settlement non disponibile' },
                { value: 'da_saldare', label: 'Da saldare' },
                { value: 'saldato', label: 'Saldato' },
              ],
            },
            {
              id: 'client',
              value: filters.clientFilter,
              onChange: setClientFilter,
              placeholder: 'Cliente',
              options: [
                { value: 'all', label: 'Tutti i clienti' },
                ...clients.map((client) => ({
                  value: String(client.id),
                  label: client.company || client.businessName || client.name,
                })),
              ],
            },
            {
              id: 'assignee',
              value: filters.assigneeFilter,
              onChange: setAssigneeFilter,
              placeholder: 'Responsabile',
              options: [
                { value: 'all', label: 'Tutti i responsabili' },
                ...assigneeOptions.map((assignee) => ({
                  value: assignee,
                  label: assignee,
                })),
              ],
            },
            {
              id: 'stage',
              value: filters.stageFilter,
              onChange: setStageFilter,
              placeholder: 'Fase',
              options: [
                { value: 'all', label: 'Tutte le fasi' },
                ...visibleStages.map((stage) => ({
                  value: stage,
                  label: stage,
                })),
              ],
            },
          ]}
        />
        <PageDivider />
        </>
      ) : null}

      {!completedDealsModuleDisabled && !completedDealsTenantDatabaseBlocked ? (
        <CompletedBoard
          visibleStages={visibleStages}
          dealsWithoutSettlement={moduleFilters.filterRows(dealsWithoutSettlement)}
          dealsToSettle={moduleFilters.filterRows(dealsToSettle)}
          settledDeals={moduleFilters.filterRows(settledDeals)}
          settlementBatchState={settlementBatchState}
          onCardClick={openDealDetails}
          onOpenClient={openClientDetail}
          onOpenStaff={openResponsibleStaff}
          onDragEnd={onDragEnd}
          staff={staff}
        />
      ) : null}

      {!completedDealsModuleDisabled && !completedDealsTenantDatabaseBlocked ? (
        <CompletedDetailDialog
          deal={selectedDeal}
          isOpen={isDetailDialogOpen}
          onClose={closeDealDetails}
          onOpenClient={openClientDetail}
          onOpenStaff={openResponsibleStaff}
          onCreateInvoice={openCompletedDealCreateInvoice}
          onOpenAccounting={openCompletedDealAccounting}
          onDelete={async (dealId) => {
            setDeleteRequest({ id: dealId });
          }}
          onUpdateStaff={handleUpdateCompletedDealStaff}
          staff={staff}
          staffCatalogStatus={staffCatalogStatus}
          canManageDealDestructiveActions={capabilities?.canManageDealDestructiveActions === true}
        />
      ) : null}

      <ConfirmActionDialog
        open={Boolean(deleteRequest)}
        title="Eliminare l'affare completato?"
        description="L'affare completato verrà rimosso definitivamente."
        confirmLabel="Elimina"
        cancelLabel="Annulla"
        tone="danger"
        onCancel={() => setDeleteRequest(null)}
        onConfirm={async () => {
          const result = await handleDeleteCompletedDeal(deleteRequest.id);
          setDeleteRequest(null);
          toast({
            title: result.ok ? 'Affare eliminato' : 'Eliminazione non riuscita',
            description: result.message,
            variant: result.ok ? 'default' : 'destructive',
          });
        }}
      />
    </PageFrame>
  );
};

export default CompletedDealsPage;
